import React, { useState } from 'react';
import { Button, Form, Input, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { RuleObject } from 'antd/es/form';
import styles from './ResetPasswordForm.module.css';

// new password for reset password
export interface ResetPasswordInfo {
  newPassword: string
  confirmNewPassword: string
};

export interface ResetPasswordFormProps {
  handleResetPassword: (info: ResetPasswordInfo) => Promise<void>
};

// form used on reset password page
const ResetPasswordForm = (props: ResetPasswordFormProps): JSX.Element | null => {
  // password input visibility state
  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  // reset password call enabled/disabled state
  const [resetPasswordDisabled, setResetPasswordDisabled] = useState<boolean>(false);

  // handle reset password request
  const handleResetPassword = (info: ResetPasswordInfo) => {
    if (resetPasswordDisabled) {
      return;
    }

    setResetPasswordDisabled(true);
    props.handleResetPassword(info).finally(
      () => setResetPasswordDisabled(false)
    );
  };

  return (
    <Form
      name='basic'
      layout={'vertical'}
      initialValues={{ remember: true }}
      onFinish={handleResetPassword}
    >
      <Form.Item
        label={
          <Typography.Text
            className={styles.formLabel}
          >
            New Password
          </Typography.Text>
        }
        name='newPassword'
        rules={[
          {
            required: true,
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Please enter a new password
              </Typography.Text>
            )
          }
        ]}
      >
        <Input.Password
          name='password'
          placeholder='Enter password'
          prefix={<LockOutlined />}
          visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
          disabled={resetPasswordDisabled}
        />
      </Form.Item>
      <Form.Item
        label={
          <Typography.Text
            className={styles.formLabel}
          >
            Confirm New Password
          </Typography.Text>
        }
        name='confirmNewPassword'
        dependencies={['newPassword']}
        rules={[
          {
            required: true,
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Please confirm your new password
              </Typography.Text>
            )
          },
          ({ getFieldValue }) => ({
            message: (
              <Typography.Text
                className={styles.formAlert}
              >
                Passwords do not match
              </Typography.Text>
            ),
            validator: (rule: RuleObject, value: any) => {
              if (!value || getFieldValue('newPassword') === value) {
                return Promise.resolve();
              }

              return Promise.reject(
                new Error('Passwords do not match')
              );
            }
          })
        ]}
      >
        <Input.Password
          name='confirmPassword'
          placeholder='Re-enter password'
          prefix={<LockOutlined />}
          visibilityToggle={{ visible: passwordVisible, onVisibleChange: setPasswordVisible }}
          disabled={resetPasswordDisabled}
        />
      </Form.Item>
      <Form.Item className={styles.submitButtonContainer}>
        <Button
          type='primary'
          htmlType='submit'
          loading={resetPasswordDisabled}
          className='mainButton'
        >
          Reset Password
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ResetPasswordForm;
